import { Component, ChangeDetectionStrategy, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { HubConnectionState } from '@microsoft/signalr';
import { differenceInHours, format } from 'date-fns';
import { map } from 'rxjs';
import packageJson from '../../../package.json';
import { AppUpdateService } from './app-update.service';
import { HealthService } from './health.service';
import { HEALTH_CHECK_LABELS } from './health-check-response.model';
import { HealthStatus } from './health-status.enum';
import { SignalRService } from '../shared/access/signalr.service';
import { PortfoliosService } from '../counterparty-data/portfolios/portfolios.service';
import { SecuritiesService } from '../counterparty-data/securities/securities.service';

interface StatusChip {
  // Dot colour; also applied to the label when `alert` is true.
  color: string;
  label: string;
  title: string;
  pulse: boolean;
  alert: boolean;
}

const CACHE_STALE_HOURS = 24;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'of-footer',
  template: `
    <footer class="of-footer">
      @if (live(); as chip) {
        <span class="of-footer__chip" [title]="chip.title">
          <span
            class="of-conn-dot of-footer__dot"
            [class.of-conn-dot--pulse]="chip.pulse"
            [style.background]="chip.color"
          ></span>
          <span [style.color]="chip.alert ? chip.color : null">{{ chip.label }}</span>
        </span>
      }

      @if (api(); as chip) {
        <span class="of-footer__chip" [title]="chip.title">
          <span class="of-conn-dot of-footer__dot" [style.background]="chip.color"></span>
          <span [style.color]="chip.alert ? chip.color : null">{{ chip.label }}</span>
        </span>
      }

      @if (refData(); as chip) {
        <span class="of-footer__chip" [title]="chip.title">
          <i class="bi bi-database"></i>
          <span [style.color]="chip.alert ? chip.color : null">{{ chip.label }}</span>
        </span>
      }

      @if (updateAvailable()) {
        <button
          type="button"
          class="of-footer__update"
          title="A new version of Order Flow has been downloaded — reload to use it"
          (click)="reload()"
        >
          <i class="bi bi-arrow-clockwise"></i>
          Update available
        </button>
      }

      <span class="of-footer__copy">&copy; 2026 CIGP</span>
      <span class="of-footer__version">v{{ version }}</span>
    </footer>
  `,
  styles: [
    `
      .of-footer {
        --topbar-bg: rgba(255, 255, 255, 0.82);
        --topbar-border: rgba(0, 0, 0, 0.07);
        --topbar-muted: #555555;

        align-items: center;
        backdrop-filter: blur(14px) saturate(1.5);
        -webkit-backdrop-filter: blur(14px) saturate(1.5);
        background: var(--topbar-bg);
        border-top: 1px solid var(--topbar-border);
        box-shadow: 0 -1px 0 rgba(0, 0, 0, 0.04);
        display: flex;
        flex-shrink: 0;
        gap: 0.75rem;
        justify-content: flex-start;
        padding: 0.1rem 1rem;
        position: sticky;
        bottom: 0;
      }

      :host-context([data-bs-theme='dark']) .of-footer {
        --topbar-bg: #0f0f0f;
        --topbar-border: rgba(255, 255, 255, 0.07);
        --topbar-muted: #9c9488;

        backdrop-filter: none;
        -webkit-backdrop-filter: none;
        box-shadow: 0 -1px 0 rgba(255, 255, 255, 0.04);
      }

      .of-footer__chip,
      .of-footer__update,
      .of-footer__copy,
      .of-footer__version {
        color: var(--topbar-muted);
        font-size: 10px;
        letter-spacing: 0.01em;
      }

      .of-footer__chip,
      .of-footer__update {
        align-items: center;
        display: inline-flex;
        gap: 0.25rem;
        line-height: 1;
        white-space: nowrap;
      }

      /* Pushes the copyright/version block to the right edge, keeping the status
         chips left-aligned without needing a spacer element. */
      .of-footer__copy {
        margin-left: auto;
      }

      .of-footer__version {
        font-variant-numeric: tabular-nums;
      }

      /* Smaller than the 8px .of-conn-dot used in the navbar so the footer height is unchanged. */
      .of-footer__dot {
        height: 6px;
        width: 6px;
      }

      .of-footer__update {
        background: none;
        border: none;
        color: var(--bs-warning-text-emphasis);
        cursor: pointer;
        font-weight: 600;
        padding: 0;

        &:hover {
          text-decoration: underline;
        }
      }
    `
  ]
})
export class FooterComponent {
  private readonly appUpdateService = inject(AppUpdateService);
  private readonly healthService = inject(HealthService);
  private readonly signalRService = inject(SignalRService);
  private readonly portfoliosService = inject(PortfoliosService);
  private readonly securitiesService = inject(SecuritiesService);

  public version = packageJson.version;

  protected readonly updateAvailable = this.appUpdateService.updateAvailable;

  private readonly connectionState = toSignal(this.signalRService.connectionState$, {
    initialValue: HubConnectionState.Disconnected
  });

  // Null only until the first /health poll answers; the footer itself is rendered after that
  // first check has already succeeded, so a null here means a later poll failed.
  private readonly health = toSignal(this.healthService.getHealthCheckResponse(), { initialValue: null });

  // The services keep the ISO timestamp in a plain field and only signal "cache written" through
  // the boolean subject, so re-read the field on each emission.
  private readonly securitiesCachedAt = toSignal(
    this.securitiesService.securitiesSet$.pipe(map(() => this.securitiesService.securitiesSet ?? null)),
    { initialValue: null }
  );
  private readonly portfoliosCachedAt = toSignal(
    this.portfoliosService.portfoliosSet$.pipe(map(() => this.portfoliosService.portfoliosSet ?? null)),
    { initialValue: null }
  );

  protected readonly live = computed<StatusChip>(() => {
    const state = this.connectionState();
    if (state === HubConnectionState.Connected) {
      return {
        color: 'var(--bs-success)',
        label: 'Live',
        title: 'Live order updates connected',
        pulse: false,
        alert: false
      };
    }
    if (state === HubConnectionState.Connecting || state === HubConnectionState.Reconnecting) {
      return {
        color: 'var(--bs-warning-text-emphasis)',
        label: 'Reconnecting',
        title: 'Reconnecting to live order updates',
        pulse: true,
        alert: true
      };
    }
    return {
      color: 'var(--bs-danger-text-emphasis)',
      label: 'Offline',
      title: 'Live order updates disconnected — the grid may not reflect the latest changes',
      pulse: false,
      alert: true
    };
  });

  protected readonly api = computed<StatusChip>(() => {
    const health = this.health();
    if (!health) {
      return {
        color: 'var(--bs-danger-text-emphasis)',
        label: 'API unreachable',
        title: 'The last health check to the backend failed',
        pulse: false,
        alert: true
      };
    }

    // Keyed by whatever checks the API registered, so a new backend check shows up here
    // without a frontend change.
    const checks = Object.entries(health.results ?? {});
    const title = checks.length
      ? checks
          .map(([key, item]) => {
            const name = HEALTH_CHECK_LABELS[key] ?? key;
            const detail = item.status === HealthStatus.Healthy ? '' : ` (${item.description ?? 'no detail'})`;
            return `${name}: ${item.status}${detail}`;
          })
          .join(' · ')
      : 'No health checks reported';

    if (health.status === HealthStatus.Healthy) {
      return { color: 'var(--bs-success)', label: 'API', title, pulse: false, alert: false };
    }

    // Name the failing checks inline when there are only one or two — beyond that the label
    // gets too long for the footer and the tooltip carries the detail.
    const failing = checks
      .filter(([, item]) => item.status !== HealthStatus.Healthy)
      .map(([key]) => HEALTH_CHECK_LABELS[key] ?? key);
    const suffix = failing.length > 0 && failing.length <= 2 ? ` (${failing.join(', ')})` : '';

    if (health.status === HealthStatus.Degraded) {
      return {
        color: 'var(--bs-warning-text-emphasis)',
        label: `API degraded${suffix}`,
        title,
        pulse: false,
        alert: true
      };
    }
    return {
      color: 'var(--bs-danger-text-emphasis)',
      label: `API unhealthy${suffix}`,
      title,
      pulse: false,
      alert: true
    };
  });

  // Age of the cached reference data (securities + portfolios) — reported as the older of the
  // two, since that is the point from which anything on screen could be out of date.
  protected readonly refData = computed<StatusChip | null>(() => {
    const stamps = [this.securitiesCachedAt(), this.portfoliosCachedAt()]
      .filter((value): value is string => !!value)
      .map((value) => new Date(value))
      .filter((date) => !Number.isNaN(date.getTime()));

    if (stamps.length === 0) return null;

    const oldest = new Date(Math.min(...stamps.map((date) => date.getTime())));
    const stale = differenceInHours(new Date(), oldest) >= CACHE_STALE_HOURS;

    return {
      color: 'var(--bs-warning-text-emphasis)',
      label: format(oldest, 'dd MMM HH:mm'),
      title:
        `Reference data cached — securities: ${this.formatStamp(this.securitiesCachedAt())}, ` +
        `portfolios: ${this.formatStamp(this.portfoliosCachedAt())}` +
        (stale ? ' (over a day old — use Refresh cache in the navbar)' : ''),
      pulse: false,
      alert: stale
    };
  });

  protected reload(): void {
    window.location.reload();
  }

  private formatStamp(value: string | null): string {
    if (!value) return 'not loaded';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 'not loaded' : format(date, 'dd MMM HH:mm');
  }
}
